/* eslint-disable no-console, no-undef */
import React, { useCallback, useRef, useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Switch, notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import {
  decodeFrontEndMessage,
  decodeBatchedFrontEndMessage,
  computeDebounceKey,
  isFaultMessage,
} from "../../../flatbuffers/decoders/frontEndMessageDecoder";
import { isEnvelope, unwrapEnvelope } from "../../../flatbuffers/envelope";
import trackLiftBucket, { isBankIdTraceEnabled } from "./trackLiftBucket";
import { FrontEndMessageT } from "../../../flatbuffers/generated/front-end/front-end-message";
import { FrontEndMessageT as FrontEndMessageTLegacy } from "../../../flatbuffers/generated_legacy/front-end/front-end-message";
import {
  applyDeltaToState,
  createDefaultState,
  DisplayMapMode,
} from "../../../flatbuffers/decoders/applyDeltaToState";
import {
  applyDeltaToState as applyDeltaToStateLegacy,
  createDefaultState as createDefaultStateLegacy,
} from "../../../flatbuffers/decoders/applyDeltaToStateLegacy";
import { decodeFrontEndMessage as decodeFrontEndMessageLegacy } from "../../../flatbuffers/decoders/frontEndMessageDecoderLegacy";
import { SocketMessageInterface as LegacySocketMessageInterface } from "../../../interfaces/Monitoring.interface.legacy";
import { UuidT as CanonicalUuidT } from "../../../flatbuffers/generated/front-end/uuid";
import {
  translateControlValue,
  translateFaults,
  translateService,
  translateUsageValue,
} from "../../../flatbuffers/decoders/legacyMonitoringBridge";
import CommandSentNotificationDetails from "./CommandSentNotificationDetails";
import { buildExclusionMap, ExclusionKey, ExclusionRule } from "../../../utils/buildExclusionMap";
import { OneHotExclusionConfigT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-config";
import { OneHotExclusionGroupT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-group";
import { OneHotExclusionEntryT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-entry";
import { MessageT } from "../../../flatbuffers/generated/ln2/message";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
import { emitUiCmdTx } from "../socketMessageState/uiCmdTxBus";
import { FaultMessageT } from "../../../flatbuffers/generated/fault/fault-message";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
import { emitWebSocketMessage } from "../socketMessageState/webSocketMessageBus";
import socketEmitter from "./MonitoringWebSocketEmitter";
// CHANGE DETECTION
import hasStateChanged from "./hasStateChanged";
// CUSTOM HOOKS
import useGetAllKeys from "../../../customHooks/GET_requests/useGetAllKeys";
import useGetDebounceOverrides from "../../../customHooks/GET_requests/useGetDebounceOverrides";
import resolveDebounceTime, {
  debounceKey,
  deviceTypeFromLiftType,
} from "../../../utils/debounceLookup";
// INTERFACES
import { store, RootState } from "../../../app/store";
import {
  NO_ACK_VERDICT,
  LayoutInterface,
  SocketMessageInterface,
} from "../../../interfaces/Monitoring.interface";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { FaultVariant } from "../../../flatbuffers/generated/fault/fault-variant";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { ElevatorFaultMessageT } from "../../../flatbuffers/generated/fault/elevator-fault-message";
import { EscalatorFaultMessageT } from "../../../flatbuffers/generated/fault/escalator-fault-message";
import { NormalFaultT } from "../../../flatbuffers/generated/fault/normal-fault";
import { ValuedFaultT } from "../../../flatbuffers/generated/fault/valued-fault";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { BitMaskMessageT } from "../../../flatbuffers/generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../../../flatbuffers/generated/ln2/one-hot-message";
import { uuidToString } from "../../../utils/uuid";
import {
  compoundKey,
  bankKey,
  faultKey,
  faultKeyPrefix,
  BankKey,
  CompoundKey,
  FaultKey,
} from "../../../utils/typedKeys";
import { devLog } from "../../../utils/devLog";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// INTEGRATION TRACE
import { tracePointEmit } from "../../../integrationTrace";
import { TracePointId } from "../../../flatbuffers/generated/integration-trace/trace-point-id";
import type { HallCallBinding, HallCallSlot } from "../../../interfaces/IconDefinitions";

const HALL_CALL_SLOTS: HallCallSlot[] = ["upFront", "upRear", "downFront", "downRear"];

const FAULT_PERSISTENCE_KEY_PREFIX = "liftnet_expired_faults_";

// Map FaultContent to numeric device type for composite debounce key
const faultContentToDeviceType = (fc: FaultContent): number => {
  switch (fc) {
    case FaultContent.ElevatorFaultMessage:
      return 0; // DEVICE_ELEVATOR
    case FaultContent.EscalatorFaultMessage:
      return 1; // DEVICE_ESCALATOR
    default:
      return 0;
  }
};

// LocalStorage helpers for fault persistence (stores FaultKey strings)
const getPersistedFaults = (bankIdStr: string): Set<FaultKey> => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return new Set(JSON.parse(stored) as FaultKey[]);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to load persisted faults:", e);
  }
  return new Set();
};

const savePersistedFaults = (bankIdStr: string, faults: Set<FaultKey>) => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    if (faults.size > 0) {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(faults)));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to save persisted faults:", e);
  }
};

function MonitoringWebSocket({
  serverId,
  serverName,
  channelId,
  layouts,
  createWebSocket,
  onSocketOpen = undefined,
  respectReplayPanel = true,
  connectionLabel = "EDGE (ONLINE)",
}: {
  serverId: string;
  serverName: string;
  channelId: string;
  layouts: LayoutInterface[];
  createWebSocket: () => WebSocket | Promise<WebSocket>;
  onSocketOpen?: () => void;
  respectReplayPanel?: boolean;
  connectionLabel?: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const socket = useRef<WebSocket | null>(null);
  const myIntervalCheckSocketConnection: any = useRef(null);
  const CONNECTING = 0;
  const OPEN = 1;
  const CLOSING = 2;
  const CLOSED = 3;
  const dispatch = useBankDispatch();
  const dispatchRedux = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
  // Reactive mirror of socket.current?.readyState so the Queue Info card
  // re-renders on every transition (open/close). The ref alone never triggers
  // re-renders, leaving the Status tag and Connect/Disconnect buttons stale.
  const [connectionState, setConnectionState] = useState<number>(CLOSED);

  // ************************************************ */
  // FAULT TIMER STATE ****************************** */
  const activeFaults = useRef<Map<FaultKey, ReturnType<typeof setTimeout>>>(new Map());
  const expiredFaults = useRef<Set<FaultKey>>(new Set());
  const persistedFaultsByBank = useRef<Map<string, Set<FaultKey>>>(new Map());
  const messageDebounceMap = useRef<Map<number, number>>(new Map());
  const debounceOverrideMap = useRef<Map<string, number>>(new Map());
  const prevOverrideFingerprint = useRef<string>("");
  const lastMessagesByLift = useRef<Map<string, SocketMessageInterface>>(new Map());
  const liftStateRef = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  // Separate accumulator for OLD-schema (legacy) servers: applyDeltaToStateLegacy
  // merges deltas against the OLD-typed state, which is bridged to the canonical
  // SocketMessageInterface only at dispatch time.
  const liftStateRefLegacy = useRef<Map<CompoundKey, LegacySocketMessageInterface>>(new Map());
  // Diagnostic only — off unless the ?traceBankId query parameter is present.
  // See trackLiftBucket.
  const bankIdTraceEnabled = useRef<boolean>(isBankIdTraceEnabled()).current;
  const priorStateKeyByLift = useRef<Map<string, CompoundKey>>(new Map());
  const priorStateKeyByLiftLegacy = useRef<Map<string, CompoundKey>>(new Map());
  const pendingDispatches = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  const deltaDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const rafHandle = useRef<number | null>(null);
  const displayMapModes = useRef<Map<BankKey, DisplayMapMode>>(new Map());
  // Per-bank hall-call bindings from adapter config. Absent for an adapter that
  // has configured none, which is every adapter today — applyDeltaToState then
  // leaves hallCalls exactly as it always did.
  const hallCallBindings = useRef<Map<BankKey, HallCallBinding[]>>(new Map());
  const exclusionMaps = useRef<Map<BankKey, Map<ExclusionKey, ExclusionRule>>>(new Map());
  const prevDebounceFingerprint = useRef<string>("");
  const expectingBatch = useRef<boolean>(true);
  const connecting = useRef<boolean>(false);
  // True when the user clicked Disconnect in the Queue Info card. Auto-reconnect
  // must respect this so the socket stays closed until the user clicks Connect.
  // A server-side close (network drop, ws.close() from tests) does NOT set this,
  // preserving auto-reconnect for unintentional disconnects.
  const manuallyDisconnected = useRef<boolean>(false);
  const layoutsRef = useRef<LayoutInterface[]>(layouts);
  layoutsRef.current = layouts;

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const showQueueInfo = useSelector((state: RootState) => state.monitoring.showQueueInfo);
  const stopSocketConnection = useSelector(
    (state: RootState) => state.monitoring.stopSocketConnection,
  );
  const sendCommand = useSelector((state: RootState) => state.monitoring.sendCommand);
  const showCommandDetails = useSelector((state: RootState) => state.monitoring.showCommandDetails);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { keys } = useGetAllKeys();

  // Derive stable bank IDs from layouts for debounce override fetching
  const bankIds = React.useMemo(() => layouts.map((l) => l.idBank), [layouts]);
  const { debounceOverrides } = useGetDebounceOverrides(bankIds);

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const flushPendingDispatches = useCallback(() => {
    if (respectReplayPanel && store.getState().monitoring.openReplayPanel) {
      pendingDispatches.current.clear();
      rafHandle.current = null;
      return;
    }
    // TP 412: FrontendRafFlushed — RAF callback fired, flushing pending state dispatches
    tracePointEmit(TracePointId.FrontendRafFlushed);
    pendingDispatches.current.forEach((payload) => {
      dispatch({
        type: "UPDATE_MESSAGE",
        payload,
      });
    });
    pendingDispatches.current.clear();
    rafHandle.current = null;
  }, [dispatch, respectReplayPanel]);

  const scheduleDispatch = useCallback(
    (stateKey: CompoundKey, payloadData: any) => {
      pendingDispatches.current.set(stateKey, payloadData);
      if (rafHandle.current === null) {
        // TP 411: FrontendRafScheduled — first pending dispatch, scheduling RAF flush
        tracePointEmit(TracePointId.FrontendRafScheduled);
        rafHandle.current = requestAnimationFrame(flushPendingDispatches);
      }
    },
    [flushPendingDispatches],
  );

  const getDebounceTime = useCallback(
    (
      faultContent: FaultContent,
      code: number,
      targetBankId: string,
      targetLiftId: string,
    ): number => {
      const dt = faultContentToDeviceType(faultContent);
      const composite = debounceKey(dt, MessageContent.Fault_FaultMessage, code);
      return resolveDebounceTime(
        debounceOverrideMap.current,
        messageDebounceMap.current,
        targetBankId,
        targetLiftId,
        composite,
      );
    },
    [],
  );

  const shouldFaultBlink = useCallback(
    (activeFaultList: FaultMessageT[], bankIdStr: string, targetLiftId: string): boolean => {
      const persistedFaults = persistedFaultsByBank.current.get(bankIdStr) || new Set();
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);
      const allExpiredFaults = Array.from(expiredFaults.current);
      const hasExpiredFaultsForThisLift = allExpiredFaults.some((key) => key.startsWith(prefix));
      const matchesPersistedFault = activeFaultList.some((f) =>
        persistedFaults.has(
          faultKey(bankIdStr, targetLiftId, f.faultType, f.fault?.fault?.message ?? 0),
        ),
      );

      return hasExpiredFaultsForThisLift || matchesPersistedFault;
    },
    [],
  );

  const manageFaultTimers = useCallback(
    (
      faultsWithDebounce: Array<{
        fault: FaultMessageT;
        debounceMs: number;
      }>,
      bankIdStr: string,
      targetLiftId: string,
    ) => {
      const incomingFaultKeys = new Set(
        faultsWithDebounce.map((f) =>
          faultKey(bankIdStr, targetLiftId, f.fault.faultType, f.fault.fault?.fault?.message ?? 0),
        ),
      );
      const currentFaults = activeFaults.current;
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);

      const triggerImmediateUpdate = () => {
        const key = faultKeyPrefix(bankIdStr, targetLiftId);
        const lastMsg = lastMessagesByLift.current.get(key);
        if (lastMsg) {
          const shouldBlink = shouldFaultBlink(lastMsg.activeFaults, bankIdStr, targetLiftId);
          if (!respectReplayPanel || !store.getState().monitoring.openReplayPanel) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: { ...lastMsg, faults: shouldBlink },
            });
          }
        }
      };

      const updatePersistence = (fk: FaultKey, shouldAdd: boolean) => {
        try {
          const currentSet = persistedFaultsByBank.current.get(bankIdStr) || new Set<FaultKey>();
          if (shouldAdd) currentSet.add(fk);
          else currentSet.delete(fk);
          persistedFaultsByBank.current.set(bankIdStr, currentSet);
          savePersistedFaults(bankIdStr, currentSet);
        } catch (e) {
          console.error("Error updating fault persistence:", e);
        }
      };

      // 1. START TIMERS or IMMEDIATELY EXPIRE faults.
      // No debounce = immediate blink. Configured debounce = delayed blink.
      faultsWithDebounce.forEach(({ fault, debounceMs }) => {
        const fk = faultKey(
          bankIdStr,
          targetLiftId,
          fault.faultType,
          fault.fault?.fault?.message ?? 0,
        );
        if (!currentFaults.has(fk) && !expiredFaults.current.has(fk)) {
          if (debounceMs <= 0) {
            // No debounce — expire immediately so fault blinks right away
            // TP 410: FrontendFaultTimerEvent — fault expired immediately (no debounce)
            tracePointEmit(TracePointId.FrontendFaultTimerEvent);
            expiredFaults.current.add(fk);
            updatePersistence(fk, true);
            triggerImmediateUpdate();
          } else {
            const timer = setTimeout(() => {
              // TP 410: FrontendFaultTimerEvent — debounce timer fired for fault
              tracePointEmit(TracePointId.FrontendFaultTimerEvent);
              expiredFaults.current.add(fk);
              currentFaults.delete(fk);
              updatePersistence(fk, true);
              triggerImmediateUpdate();
            }, debounceMs);
            currentFaults.set(fk, timer);
          }
        }
      });

      // 2. CANCEL TIMERS
      Array.from(currentFaults.keys()).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            const timer = currentFaults.get(fk);
            if (timer) clearTimeout(timer);
            currentFaults.delete(fk);
            updatePersistence(fk, false);
          }
        }
      });

      // 3. STOP BLINKING
      Array.from(expiredFaults.current).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            expiredFaults.current.delete(fk);
            updatePersistence(fk, false);
            triggerImmediateUpdate();
          }
        }
      });
    },
    [dispatch, shouldFaultBlink, respectReplayPanel],
  );

  // Process a single decoded FrontEndMessageT and update state/dispatch
  const processFrontEndMessage = useCallback(
    (fem: FrontEndMessageT) => {
      if (!fem.bankId || !fem.liftId || !fem.msg?.msg) return;

      const bankIdStr = uuidToString(fem.bankId!);
      const liftIdStr = uuidToString(fem.liftId!);
      const stateKey = compoundKey(fem.bankId!, fem.liftId!);
      const bk = bankKey(fem.bankId!);

      if (bankIdTraceEnabled) {
        trackLiftBucket(
          liftIdStr,
          bankIdStr,
          stateKey,
          priorStateKeyByLift.current,
          liftStateRef.current,
          "live",
        ).forEach((warning) => console.warn(warning));
      }

      const existingState =
        liftStateRef.current.get(stateKey) || createDefaultState(fem.bankId!, fem.liftId!);

      const mergedState = applyDeltaToState(
        existingState,
        fem,
        displayMapModes.current.get(bk),
        exclusionMaps.current.get(bk),
        hallCallBindings.current.get(bk),
      );

      const { activeFaults: activeFaultList } = mergedState;
      const faultsWithDebounce = activeFaultList.map((fault) => {
        const debounceMs = getDebounceTime(
          fault.faultType,
          fault.fault?.fault?.message ?? 0,
          bankIdStr,
          liftIdStr,
        );
        return { fault, debounceMs };
      });
      manageFaultTimers(faultsWithDebounce, bankIdStr, liftIdStr);

      const shouldBlink = shouldFaultBlink(activeFaultList, bankIdStr, liftIdStr);

      const isSocketClosed = socket.current?.readyState === WebSocket.CLOSED;

      const payloadData: SocketMessageInterface = {
        ...mergedState,
        faults: shouldBlink,
        connected: mergedState.connected && !isSocketClosed,
        // Carried through explicitly. This is the payload that reaches the
        // dashboard and the raw bus, so blanking it here would discard the
        // verdict on the one path that has to deliver it.
        ack: mergedState.ack ?? NO_ACK_VERDICT,
      };

      liftStateRef.current.set(stateKey, mergedState);

      const liftFaultPrefix = faultKeyPrefix(bankIdStr, liftIdStr);
      const prevPayload = lastMessagesByLift.current.get(liftFaultPrefix);
      lastMessagesByLift.current.set(liftFaultPrefix, payloadData);

      // Emit raw per-message event for diagnostic consumers (Terminal view).
      // Done BEFORE the hasStateChanged dedupe + RAF-batched dashboard dispatch
      // so the Terminal can show every WS message, not just state-changing ones.
      emitWebSocketMessage(payloadData);

      try {
        const schemaFrontEndMessageJson = JSON.stringify(
          fem,
          (_key, value) => (typeof value === "bigint" ? value.toString() : value),
          2,
        );
        const socketMessageLiftJson = JSON.stringify(
          payloadData,
          (_key, value) => (typeof value === "bigint" ? value.toString() : value),
          2,
        );
        socketEmitter.emitPreDispatchMessage({
          timestamp: Date.now(),
          bankId: bankIdStr,
          liftId: liftIdStr,
          schemaFrontEndMessageJson,
          socketMessageLiftJson,
        });
      } catch (jsonError) {
        console.error("[WS][DECODED] Failed to stringify front_end_message payload:", jsonError);
      }

      if (hasStateChanged(prevPayload, payloadData)) {
        // TP 409: FrontendStateChanged — state diffed as changed, queuing dispatch
        tracePointEmit(TracePointId.FrontendStateChanged);
        const compositeKey = computeDebounceKey(fem);
        const isFault = isFaultMessage(fem);
        let debounceMs = 0;
        if (!isFault && compositeKey !== undefined) {
          // TP 407: FrontendDebounceKeyComputed — debounce key computed for non-fault message
          tracePointEmit(TracePointId.FrontendDebounceKeyComputed);
          debounceMs = resolveDebounceTime(
            debounceOverrideMap.current,
            messageDebounceMap.current,
            bankIdStr,
            liftIdStr,
            compositeKey,
          );
        }
        if (debounceMs > 0) {
          const timerKey = `${stateKey}:${compositeKey}`;
          const existing = deltaDebounceTimers.current.get(timerKey);
          if (existing) clearTimeout(existing);

          deltaDebounceTimers.current.set(
            timerKey,
            setTimeout(() => {
              deltaDebounceTimers.current.delete(timerKey);
              const latest = lastMessagesByLift.current.get(liftFaultPrefix);
              if (latest) {
                scheduleDispatch(stateKey, latest);
              }
            }, debounceMs),
          );
        } else {
          scheduleDispatch(stateKey, payloadData);
        }
      }
    },
    [manageFaultTimers, shouldFaultBlink, getDebounceTime, scheduleDispatch],
  );

  // Legacy (OLD-schema / development server) per-message handler. Decodes the OLD single
  // FrontEndMessage with the vendored OLD generated tree, applies it via
  // applyDeltaToStateLegacy, then TRANSLATES the OLD-schema enum fields (service/fault/
  // usage/control) into the NEW types the shared UI renders — mapped BY NAME via the
  // generated enums (legacyMonitoringBridge), with the Normal-variant restructure for
  // service/fault. The shared fault-timer/blink/persistence machinery then runs on the
  // translated (NEW-structured) faults exactly as for the NEW path. A dashboard session
  // is single-format (atomic edge), so the OLD/NEW accumulators never interleave.
  const processFrontEndMessageLegacy = useCallback(
    (fem: FrontEndMessageTLegacy) => {
      if (!fem.bankId || !fem.liftId || !fem.msg?.msg) return;

      // OLD and NEW UuidT carry identical data; the key/string helpers only read .value.
      const bankUuid = fem.bankId as unknown as CanonicalUuidT;
      const liftUuid = fem.liftId as unknown as CanonicalUuidT;
      const bankIdStr = uuidToString(bankUuid);
      const liftIdStr = uuidToString(liftUuid);
      const stateKey = compoundKey(bankUuid, liftUuid);
      const bk = bankKey(bankUuid);

      if (bankIdTraceEnabled) {
        trackLiftBucket(
          liftIdStr,
          bankIdStr,
          stateKey,
          priorStateKeyByLiftLegacy.current,
          liftStateRefLegacy.current,
          "legacy",
        ).forEach((warning) => console.warn(warning));
      }

      const existingState =
        liftStateRefLegacy.current.get(stateKey) ||
        createDefaultStateLegacy(fem.bankId, fem.liftId);

      const mergedState = applyDeltaToStateLegacy(
        existingState,
        fem,
        displayMapModes.current.get(bk),
      );
      liftStateRefLegacy.current.set(stateKey, mergedState);

      // Translate the OLD-schema enum fields to NEW (by name; OLD values with no NEW
      // counterpart are dropped) and run the shared fault machinery on them.
      const translatedFaults = translateFaults(mergedState.activeFaults);
      const faultsWithDebounce = translatedFaults.map((fault) => ({
        fault,
        debounceMs: getDebounceTime(
          fault.faultType,
          fault.fault?.fault?.message ?? 0,
          bankIdStr,
          liftIdStr,
        ),
      }));
      manageFaultTimers(faultsWithDebounce, bankIdStr, liftIdStr);

      const shouldBlink = shouldFaultBlink(translatedFaults, bankIdStr, liftIdStr);
      const isSocketClosed = socket.current?.readyState === WebSocket.CLOSED;

      const payloadData: SocketMessageInterface = {
        ...mergedState,
        bankId: mergedState.bankId as unknown as SocketMessageInterface["bankId"],
        liftId: mergedState.liftId as unknown as SocketMessageInterface["liftId"],
        connected: mergedState.connected && !isSocketClosed,
        faults: shouldBlink,
        activeFaults: translatedFaults,
        serviceMode: translateService(mergedState.serviceMode),
        direction: translateUsageValue(mergedState.direction as unknown as number | null),
        doorZone: translateUsageValue(mergedState.doorZone as unknown as number | null),
        escalatorControl: translateControlValue(
          mergedState.escalatorControl as unknown as number | null,
        ),
        msg: {
          contentType: mergedState.msg.contentType,
          messageType: 0 as unknown as SocketMessageInterface["msg"]["messageType"],
          content: null,
          active: mergedState.msg.active,
        },
        usage: mergedState.usage ?? {},
        oneHotMap: mergedState.oneHotMap ?? {},
        // The OLD-schema path. That wire format has no acknowledgement fields
        // at all, so there is nothing to carry and nothing to lose: a legacy
        // server can never report a verdict, a Withheld one included.
        ack: NO_ACK_VERDICT,
        withheldCalls: [],
      };

      liftStateRef.current.set(stateKey, payloadData);
      emitWebSocketMessage(payloadData);

      try {
        const schemaFrontEndMessageJson = JSON.stringify(
          fem,
          (_key, value) => (typeof value === "bigint" ? value.toString() : value),
          2,
        );
        const socketMessageLiftJson = JSON.stringify(
          payloadData,
          (_key, value) => (typeof value === "bigint" ? value.toString() : value),
          2,
        );
        socketEmitter.emitPreDispatchMessage({
          timestamp: Date.now(),
          bankId: bankIdStr,
          liftId: liftIdStr,
          schemaFrontEndMessageJson,
          socketMessageLiftJson,
        });
      } catch (jsonError) {
        console.error(
          "[WS][DECODED] Failed to stringify legacy front_end_message payload:",
          jsonError,
        );
      }

      scheduleDispatch(stateKey, payloadData);
    },
    [getDebounceTime, manageFaultTimers, shouldFaultBlink, scheduleDispatch],
  );

  const onMessageFlatBuffer = useCallback(
    async (socketMessage: ArrayBuffer | Blob | string) => {
      if (respectReplayPanel && store.getState().monitoring.openReplayPanel) {
        return;
      }
      if (typeof socketMessage === "string") {
        console.error("❌ WS sent STRING instead of binary:", socketMessage);
        return;
      }
      let buffer: ArrayBuffer;
      if (socketMessage instanceof Blob) {
        buffer = await socketMessage.arrayBuffer();
      } else {
        buffer = socketMessage;
      }

      const raw = new Uint8Array(buffer);

      // Non-LN2E frames still map to only two schema families (NEW vs LEGACY),
      // but NEW can arrive in two raw shapes. Decode in this order:
      //   1) NEW single message
      //   2) NEW batched message
      //   3) LEGACY single message (fallback)
      // This preference preserves modern decoding when envelope wrapping is
      // disabled, while keeping backward compatibility with old dev servers.
      if (!isEnvelope(raw)) {
        const newSingleFem = decodeFrontEndMessage(raw);
        if (newSingleFem) {
          processFrontEndMessage(newSingleFem);
          return;
        }

        const rawBatch = decodeBatchedFrontEndMessage(raw);
        if (rawBatch && rawBatch.messages.length > 0) {
          rawBatch.messages.forEach((fem) => processFrontEndMessage(fem));
          return;
        }

        const legacyFem = decodeFrontEndMessageLegacy(raw);
        if (legacyFem) {
          processFrontEndMessageLegacy(legacyFem);
          return;
        }

        const firstBytes = Array.from(raw.subarray(0, 12))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join(" ");
        console.warn("[WS][DROP][NON_ENVELOPE_DECODE_FAILED]", {
          byteLength: raw.length,
          firstBytes,
        });
        return;
      }

      // Strip the 42-byte LN2E envelope header (new cloud servers).
      const bytes = unwrapEnvelope(raw);
      try {
        // First message after connection is a BatchedFrontEndMessage containing full state.
        // Runtime messages also arrive as BatchedFrontEndMessage (5Hz batches from cloud).
        // All messages in a batch are processed synchronously before any
        // RAF-dispatched render — pendingDispatches accumulates them as a Map
        // and the single RAF frame at the end flushes them all at once.
        const batched = decodeBatchedFrontEndMessage(bytes);
        if (expectingBatch.current && batched) {
          expectingBatch.current = false;
          if (batched.messages.length > 0) {
            // TP 419: FrontendInitialBatch — first batch (full state snapshot) received
            tracePointEmit(TracePointId.FrontendInitialBatch);

            // Each connected lift should report at least one message in the
            // initial state snapshot (status, service mode, traffic position,
            // etc.). A batch smaller than the configured lift count strongly
            // suggests the server-side initial-state push lost the race with
            // runtime broadcasts and was either dropped or replaced. Surface
            // it so the bug doesn't hide silently the way it did before.
            const expectedLiftCount = layoutsRef.current.reduce(
              (acc, l) => acc + (l.lifts?.length ?? 0),
              0,
            );
            if (expectedLiftCount > 0 && batched.messages.length < expectedLiftCount) {
              console.warn(
                `[WS] Initial batch suspiciously small: ${batched.messages.length} messages ` +
                  `for ${expectedLiftCount} configured lifts. The server-side initial-state ` +
                  `push may have been dropped or lost the race with runtime broadcasts.`,
              );
            }
            // Reset all in-memory state caches so the new initial snapshot is
            // the sole source of truth. Without this, a reconnect (edge/VTS
            // restart) overlays the new snapshot on stale state from the prior
            // session — anything present then but absent now lingers as ghost
            // data. Hard refresh works because the React app rebuilds from
            // scratch; mirror that behavior here.
            //
            // Persisted-fault localStorage state is intentionally preserved —
            // the post-batch validation block below reconciles it against the
            // fresh snapshot.
            liftStateRef.current.clear();
            priorStateKeyByLift.current.clear();
            priorStateKeyByLiftLegacy.current.clear();
            lastMessagesByLift.current.clear();
            pendingDispatches.current.clear();
            deltaDebounceTimers.current.forEach((timer) => clearTimeout(timer));
            deltaDebounceTimers.current.clear();
            activeFaults.current.forEach((timer) => clearTimeout(timer));
            activeFaults.current.clear();
            dispatch({ type: "CLEAR_MESSAGES" });

            devLog(`[WS] Initial batch received: ${batched.messages.length} messages`);
            batched.messages.forEach((fem, idx) => {
              const encoded = fem.msg?.msg;
              let msgLabel = "unknown";
              if (encoded) {
                if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
                  const bm = encoded.content as BitMaskMessageT;
                  const outerType = MessageContent[bm?.messageType] ?? bm?.messageType;
                  msgLabel = `BM::${outerType}`;

                  // Expand fault messages to full path
                  if (bm?.messageType === MessageContent.Fault_FaultMessage) {
                    const faultMsg = bm.message as FaultMessageT;
                    const deviceType =
                      FaultContent[faultMsg.faultType] ?? `FaultContent(${faultMsg.faultType})`;
                    const inner = faultMsg.fault as
                      ElevatorFaultMessageT | EscalatorFaultMessageT | null;
                    if (inner) {
                      const variant =
                        FaultVariant[inner.faultType] ?? `FaultVariant(${inner.faultType})`;
                      const faultInner = inner.fault as NormalFaultT | ValuedFaultT | null;
                      const faultEnum =
                        faultInner != null
                          ? (Fault[faultInner.message] ?? `Fault(${faultInner.message})`)
                          : "null";
                      msgLabel = `BM::Fault::${deviceType}::${variant}::${faultEnum}`;
                    } else {
                      msgLabel = `BM::Fault::${deviceType}::null`;
                    }
                  }
                } else if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
                  const oh = encoded.content as OneHotMessageT;
                  msgLabel = `OH::${MessageContent[oh?.messageType] ?? oh?.messageType}`;
                  if (oh?.messageType === MessageContent.Usage_UsageMessage) {
                    const liftName = fem.liftId?.value?.upper?.toString().slice(-4) ?? "?";
                    devLog(
                      `[WS:OH:Usage] lift=${liftName} active=${fem.msg?.active} msg=${msgLabel}`,
                    );
                  }
                }
              }
              devLog(`[WS] batch[${idx}] active=${fem.msg?.active} ${msgLabel}`);
              processFrontEndMessage(fem);
            });

            // After processing the initial batch, validate persisted faults
            // against the actual bitmask state. Any persisted fault that is
            // NOT active in the current state should be cleared — it was
            // resolved while we were offline.
            persistedFaultsByBank.current.forEach((faultSet, bankIdStr) => {
              const keysToRemove: FaultKey[] = [];
              faultSet.forEach((fk) => {
                // Check if this persisted fault is still in expiredFaults
                // (which means manageFaultTimers confirmed it during batch processing).
                // If manageFaultTimers cleared it (fault went inactive), remove from persistence.
                if (!expiredFaults.current.has(fk)) {
                  keysToRemove.push(fk);
                }
              });
              if (keysToRemove.length > 0) {
                keysToRemove.forEach((fk) => faultSet.delete(fk));
                savePersistedFaults(bankIdStr, faultSet);
                if (process.env.NODE_ENV === "development") {
                  // eslint-disable-next-line no-console
                  console.log(
                    `[FAULT_TIMER] Cleared ${keysToRemove.length} stale persisted faults for bank ${bankIdStr}`,
                  );
                }
              }
            });

            return;
          }
          devLog("[WS] Initial batch received: 0 messages (empty state)");
          return;
        }
        if (expectingBatch.current) {
          expectingBatch.current = false;
        }
        if (batched && batched.messages.length > 0) {
          batched.messages.forEach((fem) => processFrontEndMessage(fem));
        }
      } catch (err) {
        console.error("❌ Decoder FAILED", err);
      }
    },
    [processFrontEndMessage, processFrontEndMessageLegacy, respectReplayPanel],
  );

  // Keep stable refs for callbacks that change identity frequently.
  // The socket event handlers read through these refs so the socket
  // lifecycle useEffect never re-runs due to callback identity changes.
  const onMessageRef = useRef(onMessageFlatBuffer);
  onMessageRef.current = onMessageFlatBuffer;

  const createWebSocketRef = useRef(createWebSocket);
  createWebSocketRef.current = createWebSocket;

  const onSocketOpenRef = useRef(onSocketOpen);
  useEffect(() => {
    onSocketOpenRef.current = onSocketOpen;
  }, [onSocketOpen]);

  const setupSocket = useCallback((ws: WebSocket) => {
    // If a previous socket exists (e.g. race between two effects), close it
    // before adopting the new one so we never have two live connections.
    if (socket.current && socket.current !== ws) {
      socket.current.close();
    }
    connecting.current = false;
    expectingBatch.current = true;
    socket.current = ws;
    // Seed connectionState from the freshly-created socket. Constructors
    // begin in CONNECTING, so this matches what the open event will replace.
    setConnectionState(ws.readyState);
    ws.addEventListener("open", () => {
      setConnectionState(WebSocket.OPEN);
      onSocketOpenRef.current?.();
    });
    ws.addEventListener("close", () => {
      setConnectionState(WebSocket.CLOSED);
    });
    ws.addEventListener("message", (event: any) => {
      // Drop straggler messages that arrive after close() was called but before
      // the close event has fired. Without this guard, an in-flight batch can
      // dispatch fresh state into Redux after the user clicked Disconnect,
      // causing monitoring animations to keep updating for one more tick.
      if (ws.readyState !== WebSocket.OPEN) return;
      onMessageRef.current(event.data);
    });
  }, []);

  const onConnect = useCallback(() => {
    if (connecting.current) return;
    if (socket.current?.readyState === WebSocket.OPEN) return;
    // User (or automated reconnect) wants the socket alive — clear any prior
    // manual-disconnect intent so the auto-reconnect interval resumes working.
    manuallyDisconnected.current = false;
    connecting.current = true;
    setConnectionState(WebSocket.CONNECTING);
    const result = createWebSocketRef.current();
    if (result instanceof Promise) {
      result.then(setupSocket).catch(() => {
        connecting.current = false;
        setConnectionState(WebSocket.CLOSED);
      });
    } else {
      setupSocket(result);
    }
  }, [setupSocket]);

  const onDisconnect = useCallback(() => {
    // User-initiated disconnect: record intent so the 30s auto-reconnect
    // interval skips this socket until the user clicks Connect.
    manuallyDisconnected.current = true;
    connecting.current = false;
    setConnectionState(WebSocket.CLOSING);
    socket.current?.close();
  }, []);

  // Use a ref for enabledAutoRefresh so the interval callback is always current
  const enabledAutoRefreshRef = useRef(enabledAutoRefresh);
  enabledAutoRefreshRef.current = enabledAutoRefresh;

  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;

  // No application-level heartbeat is sent on this socket. Its upstream
  // direction carries UiCmd FlatBuffers only: the edge length-prefixes each
  // frame and VTS decodes it as a UiCmd, so an untyped byte arrives there as
  // 00 00 00 01 00 and fails to parse. Liveness is already handled a layer
  // down — the edge sends an RFC 6455 ping every 54s against a 90s read
  // deadline, and the browser answers with an automatic pong.

  useEffect(() => {
    if (keys && keys.length > 0) {
      const map = new Map<number, number>();
      keys.forEach((key: any) => {
        const dTime = key.debounceTime ?? key.debounce_time;
        const mc = key.messageContent ?? key.message_content;
        const mv = key.messageValue ?? key.message_value;
        const devType: LiftType = key.fbsDeviceType ?? key.fbs_device_type ?? LiftType.ELEVATOR;
        if (dTime && mc && mv !== undefined) {
          const dt = deviceTypeFromLiftType(devType);
          const composite = debounceKey(dt, mc, mv);
          map.set(composite, dTime);
        }
      });

      // Skip if the debounce map hasn't actually changed
      const fingerprint = JSON.stringify(Array.from(map.entries()).sort());
      if (fingerprint === prevDebounceFingerprint.current) return;
      prevDebounceFingerprint.current = fingerprint;

      messageDebounceMap.current = map;

      // Reset active timers so faults re-evaluate with correct debounce values.
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();

      // Clear race-condition faults (added with debounce=0 before map loaded)
      // but restore any persisted faults from localStorage so they keep blinking.
      expiredFaults.current.clear();
      persistedFaultsByBank.current.forEach((faultSet) => {
        faultSet.forEach((fk) => expiredFaults.current.add(fk));
      });

      dispatch({ type: "UPDATE_DEBOUNCE_MAP", payload: map });
    }
  }, [keys]);

  // Build per-bank/per-lift debounce override map
  useEffect(() => {
    if (!debounceOverrides || debounceOverrides.length === 0) return;

    const map = new Map<string, number>();
    debounceOverrides.forEach((override: any) => {
      const mc = override.messageContent ?? override.message_content;
      const mv = override.messageValue ?? override.message_value;
      const devType: LiftType =
        override.fbsDeviceType ?? override.fbs_device_type ?? LiftType.ELEVATOR;
      if (!mc || mv === undefined) return;

      const dt = deviceTypeFromLiftType(devType);
      const composite = debounceKey(dt, mc, mv);
      // FlatBuffer T-classes expose UUIDs as UuidT objects (or strings on legacy paths);
      // stringify before using as a Map key so it matches the WS-side lookup key.
      const bankIdStr =
        typeof override.bankId === "string" ? override.bankId : uuidToString(override.bankId);
      if (override.liftId) {
        const liftIdStr =
          typeof override.liftId === "string" ? override.liftId : uuidToString(override.liftId);
        map.set(`${bankIdStr}:${liftIdStr}:${composite}`, override.debounceTime);
      } else {
        map.set(`${bankIdStr}:*:${composite}`, override.debounceTime);
      }
    });

    const fingerprint = JSON.stringify(Array.from(map.entries()).sort());
    if (fingerprint === prevOverrideFingerprint.current) return;
    prevOverrideFingerprint.current = fingerprint;

    // TP 417: FrontendDebounceOverrideUpdate — per-bank/lift override map rebuilt
    tracePointEmit(TracePointId.FrontendDebounceOverrideUpdate);
    debounceOverrideMap.current = map;

    // Reset active timers so faults re-evaluate with override values.
    activeFaults.current.forEach((timer) => clearTimeout(timer));
    activeFaults.current.clear();

    expiredFaults.current.clear();
    persistedFaultsByBank.current.forEach((faultSet) => {
      faultSet.forEach((fk) => expiredFaults.current.add(fk));
    });
  }, [debounceOverrides]);

  // Load persisted faults, display map modes, and exclusion maps whenever layouts change
  useEffect(() => {
    layouts.forEach((layout) => {
      const persisted = getPersistedFaults(layout.idBank);
      if (persisted.size > 0) {
        persistedFaultsByBank.current.set(layout.idBank, persisted);
        // Pre-populate expiredFaults so blinking starts immediately on refresh
        persisted.forEach((fk) => expiredFaults.current.add(fk));
      }
      // Cache per-bank display map mode from adapter config
      const adapter = (layout as any).adapter;
      const mode = adapter?.socket_message_priority?.displayMapMode ?? "accumulate";
      displayMapModes.current.set(layout.idBank as BankKey, mode);

      // Hall-call bindings: which LN2 message this controller reports a hall
      // call with. Read defensively — this is operator-authored JSON, and a
      // malformed entry must not take the monitoring socket down.
      const rawBindings = adapter?.socket_message_priority?.hallCallBindings;
      if (Array.isArray(rawBindings)) {
        const valid = rawBindings.filter(
          (b: unknown): b is HallCallBinding =>
            typeof b === "object" &&
            b !== null &&
            typeof (b as HallCallBinding).contentType === "number" &&
            typeof (b as HallCallBinding).messageType === "number" &&
            typeof (b as HallCallBinding).message === "number" &&
            HALL_CALL_SLOTS.includes((b as HallCallBinding).slot),
        );
        hallCallBindings.current.set(layout.idBank as BankKey, valid);
      }

      // Build per-bank exclusion map from adapter's one_hot_exclusion config.
      // The adapter JSON includes the full MessageT structure for each member.
      try {
        const rawExclusion = adapter?.one_hot_exclusion;
        if (rawExclusion?.groups) {
          const config = new OneHotExclusionConfigT(
            rawExclusion.groups.map(
              (g: any) =>
                new OneHotExclusionGroupT(
                  g.category ?? g.Category ?? 0,
                  g.category_wide ?? g.CategoryWide ?? false,
                  (g.entries ?? g.Entries ?? []).map(
                    (e: any) =>
                      new OneHotExclusionEntryT(
                        (e.members ?? e.Members ?? []).map(
                          (m: any) =>
                            new MessageT(
                              m.content_type ?? m.contentType ?? m.ContentType ?? 0,
                              m.content ?? m.Content ?? null,
                            ),
                        ),
                      ),
                  ),
                ),
            ),
          );
          const map = buildExclusionMap(config);
          if (map.size > 0) {
            exclusionMaps.current.set(layout.idBank as BankKey, map);
          }
        }
      } catch {
        // Adapter exclusion config not parseable — default groups will apply
      }
    });
  }, [layouts]);

  // Socket connection lifecycle -- only depends on serverId.
  // All callbacks are accessed through stable refs so that identity changes
  // in useCallback chains never cause socket teardown + reconnect, which
  // would discard the LiftCloud hashmap replay (serviceMode, etc.).
  useEffect(() => {
    if (serverId) {
      onConnectRef.current();
      myIntervalCheckSocketConnection.current = setInterval(() => {
        if (
          socket.current?.readyState === WebSocket.CLOSED &&
          enabledAutoRefreshRef.current &&
          !manuallyDisconnected.current
        ) {
          onConnectRef.current();
        }
      }, 30 * 1000);
    }
    return () => {
      connecting.current = false;
      socket.current?.close();
      if (myIntervalCheckSocketConnection.current)
        clearInterval(myIntervalCheckSocketConnection.current);
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();
      expiredFaults.current.clear();
      liftStateRef.current.clear();
      priorStateKeyByLift.current.clear();
      priorStateKeyByLiftLegacy.current.clear();
      if (rafHandle.current !== null) {
        cancelAnimationFrame(rafHandle.current);
        rafHandle.current = null;
      }
      pendingDispatches.current.clear();
    };
  }, [serverId]);

  useEffect(() => {
    if (stopSocketConnection) socket.current?.close();
    else onConnectRef.current();
  }, [stopSocketConnection]);

  // Send command effect
  useEffect(() => {
    const targetBankId = sendCommand?.body?.bankId;
    const commandServerMatches = !sendCommand?.serverId || sendCommand.serverId === serverId;
    const bankBelongsToThisSocket =
      !!targetBankId && layouts.some((layout) => layout.idBank === targetBankId);
    const shouldHandleCommand =
      sendCommand.sendCommand && !!targetBankId && commandServerMatches && bankBelongsToThisSocket;

    if (!shouldHandleCommand) {
      return;
    }

    const activeSocket = socket.current;
    const socketIsOpen = activeSocket?.readyState === WebSocket.OPEN;

    if (!activeSocket || !channelId || !socketIsOpen) {
      notification.error({
        message: "Command failed via WebSocket",
        placement: "top",
        duration: 10,
        ...(showCommandDetails
          ? {
              style: { width: 760, maxWidth: "96vw" },
              description: (
                <CommandSentNotificationDetails
                  serverName={serverName}
                  serverId={serverId}
                  command={sendCommand.body.command}
                  position={sendCommand.body.position}
                  active={sendCommand.body.active}
                  bankId={sendCommand.body.bankId}
                  liftId={sendCommand.body.liftId}
                  channelId={channelId}
                />
              ),
            }
          : {
              description: !channelId ? "Missing channel ID" : "WebSocket connection is not open",
            }),
      });
      dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
      return;
    }

    const commandValue = sendCommand.body.command;
    const payload = createUiCmdPayload({
      message: commandValue,
      position: sendCommand.body.position,
      active: sendCommand.body.active,
      channelId,
      bankId: sendCommand.body.bankId,
      liftId: sendCommand.body.liftId,
    });

    try {
      const sentBytes = new Uint8Array(payload);
      activeSocket.send(sentBytes);
      // TP 415: FrontendUiCmdSent — UiCmd payload sent over WebSocket successfully
      tracePointEmit(TracePointId.FrontendUiCmdSent);
      // Publish the frame that actually left, for the Terminal's TX rows.
      // Deliberately after send() so a throw above means no row is claimed to
      // have gone out. emitUiCmdTx already guards each subscriber, so a buggy
      // listener cannot turn a successful command into a failed one here.
      emitUiCmdTx({
        payload: sentBytes,
        serverId,
        serverName,
        channelId,
        sentAt: Date.now(),
      });
      notification.success({
        message: "Command sent via WebSocket",
        placement: "top",
        duration: 10,
        ...(showCommandDetails
          ? {
              style: { width: 760, maxWidth: "96vw" },
              description: (
                <CommandSentNotificationDetails
                  serverName={serverName}
                  serverId={serverId}
                  command={commandValue}
                  position={sendCommand.body.position}
                  active={sendCommand.body.active}
                  bankId={sendCommand.body.bankId}
                  liftId={sendCommand.body.liftId}
                  channelId={channelId}
                />
              ),
            }
          : {}),
      });
    } catch {
      notification.error({
        message: "Command failed via WebSocket",
        placement: "top",
        duration: 10,
        ...(showCommandDetails
          ? {
              style: { width: 760, maxWidth: "96vw" },
              description: (
                <CommandSentNotificationDetails
                  serverName={serverName}
                  serverId={serverId}
                  command={commandValue}
                  position={sendCommand.body.position}
                  active={sendCommand.body.active}
                  bankId={sendCommand.body.bankId}
                  liftId={sendCommand.body.liftId}
                  channelId={channelId}
                />
              ),
            }
          : {}),
      });
    }
    dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
  }, [sendCommand, channelId, dispatchRedux, serverId, serverName, layouts, showCommandDetails]);

  return (
    <div>
      {showQueueInfo && (
        <div>
          <Card
            title={serverName}
            style={{ width: 550, marginLeft: "15px", marginTop: "10px" }}
            extra={
              <div>
                {connectionState !== OPEN && connectionState !== CLOSING && (
                  <Button onClick={onConnect} disabled={connectionState === CONNECTING}>
                    Connect
                  </Button>
                )}
                {(connectionState === OPEN || connectionState === CLOSING) && (
                  <Button onClick={onDisconnect} disabled={connectionState === CLOSING}>
                    Disconnect
                  </Button>
                )}
                <Switch
                  onChange={setEnabledAutoRefresh}
                  checked={enabledAutoRefresh}
                  checkedChildren="Reconnection"
                  unCheckedChildren="Reconnection"
                  style={{ marginLeft: "10px" }}
                />
              </div>
            }
          >
            {/* Summary Info */}
            <p style={{ marginBottom: "-1px" }}>
              <b
                className={
                  connectionState === OPEN ? "generalStyles__info" : "generalStyles__warning"
                }
              >
                Status:
              </b>{" "}
              <b style={{ color: "white" }}>
                {connectionState === OPEN && <Tag color="green">OPEN</Tag>}
                {connectionState === CONNECTING && <Tag color="cyan">CONNECTING</Tag>}
                {connectionState === CLOSING && <Tag color="orange">CLOSING</Tag>}
                {connectionState === CLOSED && <Tag color="red">CLOSED</Tag>}
              </b>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Socket Connection:</b> <span>{connectionLabel}</span>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Server:</b> <span>{serverId}</span>
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}

export default MonitoringWebSocket;
